/**
 * Note block AST and its line grammar. Pure functions only — the same grammar
 * will be ported mechanically to the Android client (see AGENTS.md).
 *
 * Line grammar (matched at line start):
 *   `# T` / `## T` / `### T`     -> heading level 1-3
 *   `- [ ] T` / `- [x] T`        -> todo
 *   `::bench <label> = <value> [unit]` -> bench mark measurement
 *   `!photo <attachmentId>`      -> photo attachment reference
 *   `!audio <attachmentId>`      -> audio attachment reference
 *   anything else                -> paragraph (consecutive lines group, blank line splits)
 */
import type { Block } from './schemas'

export const BENCH_PREFIX = '::bench '
export const PHOTO_PREFIX = '!photo '
export const AUDIO_PREFIX = '!audio '

const HEADING_RE = /^(#{1,3})\s+(.+)$/
const TODO_RE = /^- \[( |x|X)\]\s+(.*)$/
const BENCH_RE = /^::bench\s+(.+?)\s*=\s*([^=\s]+)(?:\s+(\S+))?$/
const ATTACHMENT_RE = /^!(photo|audio)\s+(\S+)\s*$/

export function parseBlocks(raw: string): Block[] {
  if (raw.trim() === '') return []
  const blocks: Block[] = []
  let paragraph: string[] = []

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', text: paragraph.join('\n') })
      paragraph = []
    }
  }

  for (const line of raw.split('\n')) {
    const heading = HEADING_RE.exec(line)
    const todo = TODO_RE.exec(line)
    const bench = BENCH_RE.exec(line)
    const attachment = ATTACHMENT_RE.exec(line)

    if (heading) {
      flushParagraph()
      const hashes = heading[1] ?? '#'
      blocks.push({ type: 'heading', level: hashes.length as 1 | 2 | 3, text: heading[2] ?? '' })
    } else if (todo) {
      flushParagraph()
      const marker = todo[1] ?? ' '
      blocks.push({ type: 'todo', done: marker.toLowerCase() === 'x', text: todo[2] ?? '' })
    } else if (bench) {
      flushParagraph()
      const parsed: Block = { type: 'bench', label: bench[1] ?? '', value: bench[2] ?? '' }
      const unit = bench[3]
      if (unit !== undefined) parsed.unit = unit
      blocks.push(parsed)
    } else if (attachment) {
      flushParagraph()
      blocks.push({
        type: 'attachment',
        kind: attachment[1] === 'audio' ? 'audio' : 'photo',
        attachmentId: attachment[2] ?? '',
      })
    } else if (line.trim() === '') {
      flushParagraph()
    } else {
      paragraph.push(line)
    }
  }
  flushParagraph()
  return blocks
}

export function serializeBlock(block: Block): string {
  switch (block.type) {
    case 'heading':
      return `${'#'.repeat(block.level)} ${block.text}`
    case 'paragraph':
      return block.text
    case 'todo':
      return `- [${block.done ? 'x' : ' '}] ${block.text}`
    case 'bench':
      return `${BENCH_PREFIX}${block.label} = ${block.value}${block.unit ? ` ${block.unit}` : ''}`
    case 'attachment':
      return `${block.kind === 'photo' ? PHOTO_PREFIX : AUDIO_PREFIX}${block.attachmentId ?? ''}`.trimEnd()
  }
}

const LINE_GROUP_TYPES = new Set(['todo', 'bench', 'attachment'])

export function serializeBlocks(blocks: readonly Block[]): string {
  let out = ''
  let prev: Block | undefined
  for (const block of blocks) {
    if (prev !== undefined) {
      // Same-type adjacent blocks form one visual group (todo list, bench
      // stack, media gallery) and rejoin with a single newline; everything
      // else gets a paragraph break.
      out += prev.type === block.type && LINE_GROUP_TYPES.has(block.type) ? '\n' : '\n\n'
    }
    out += serializeBlock(block)
    prev = block
  }
  return out
}

/** How many todo lines a raw document has (the viewer indexes checkboxes with this). */
export function countTodos(raw: string): number {
  return raw.split('\n').filter((line) => TODO_RE.test(line)).length
}

/** Flip the n-th (0-based) todo checkbox in the raw text. Returns null when out of range. */
export function toggleTodo(raw: string, index: number): string | null {
  let seen = -1
  const lines = raw.split('\n').map((line) => {
    const match = TODO_RE.exec(line)
    if (!match) return line
    seen += 1
    if (seen !== index) return line
    const marker = match[1] ?? ' '
    return marker === ' ' ? line.replace('- [ ]', '- [x]') : line.replace(/^- \[[xX]\]/, '- [ ]')
  })
  return index <= seen ? lines.join('\n') : null
}
